import { useCallback } from 'react';

import {
  panelRowMergeMode,
  type PanelSelectionMode,
} from '../session/selectionModifiers';

export type PanelRowSelectOptions = {
  /** A row with nothing pickable behind it stays inert. */
  isSelectable: boolean;
  /** Whether the row currently reads as selected — what Shift is resolved against. */
  isSelected: boolean;
  /** Selects the row's geometry under the mode its modifiers asked for. */
  onSelect: (mode: PanelSelectionMode) => void;
  /**
   * What a double-click does instead of selecting — framing the layer, for the
   * layers panel. Omitted by rows that have no second action, whose double-click
   * then just re-runs the select rather than doing something surprising.
   */
  onActivate?: () => void;
};

/**
 * One click handler for every side-panel row: layers, mappings, and whatever
 * panel comes next.
 *
 * Owns the *event* concerns common to every row — selectability, double-click,
 * suppressing the browser default — and defers the meaning of Shift to
 * `panelRowMergeMode`, the one place that rule lives, so every panel and the
 * canvas resolve a modifier the same way.
 */
export const usePanelRowSelect = ({
  isSelectable,
  isSelected,
  onSelect,
  onActivate,
}: PanelRowSelectOptions) =>
  useCallback(
    (event: React.MouseEvent) => {
      if (!isSelectable) {
        return;
      }
      // The row's name is its own control inside a larger label — the layer
      // row's checkbox owns the rest of it — so a click here must not fall
      // through to the label and toggle visibility instead.
      event.preventDefault();
      event.stopPropagation();

      if (event.detail > 1 && onActivate) {
        onActivate();
        return;
      }
      onSelect(panelRowMergeMode(event, isSelected));
    },
    [isSelectable, isSelected, onSelect, onActivate],
  );
